import {
  getGetPreferenzeUtenteQueryKey,
  useGetPreferenzeUtente,
  usePutPreferenzeUtente,
} from '@framework/api-client/api/users/preferenze-utente-controller/preferenze-utente-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { SubmitButton } from '@framework/form/form/submit/SubmitButton.tsx';
import { SwitchInput } from '@framework/form/inputs/SwitchInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import { Card, Divider, Flex, message } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { queryClient } from '../../core/network/queryClient.ts';
import { PageLayout } from '../../ui/layout/PageLayout.tsx';
import { NotificationEmailField } from './NotificationEmailField.tsx';
import {
  type UserCommunicationPreferences,
  UserCommunicationPreferencesSchema,
} from './schema/UserCommunicationPreferencesSchema.ts';

const enableAppIoChannel = process.env.ENABLE_APP_IO_CHANNEL === 'true';

/**
 * Pagina Impostazioni dell'utente frontoffice: scelta dei canali
 * per le comunicazioni di cortesia (e-mail dedicata, App IO).
 */
export function UserSettingsPage() {
  const { t } = useTranslation();

  useBreadcrumbItem({
    path: '/utente/impostazioni',
    title: t('user.settings.breadcrumb', 'Impostazioni'),
  });

  const { data: preferences, isLoading } = useGetPreferenzeUtente();

  const putMutation = usePutPreferenzeUtente({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getGetPreferenzeUtenteQueryKey());
        message.success(
          t('user.settings.save-success', 'Impostazioni salvate con successo'),
        );
      },
    },
  });

  const initialValues = useMemo(
    () =>
      UserCommunicationPreferencesSchema.cast(preferences ?? {}, {
        assert: false,
      }),
    [preferences],
  );

  const handleSubmit = async (values: UserCommunicationPreferences) => {
    try {
      await putMutation.mutateAsync({ data: values });
    } catch (e) {
      console.error(e);
      message.error(
        getNetworkErrorMessage(
          e,
          t(
            'user.settings.save-error',
            'Si è verificato un errore durante il salvataggio delle impostazioni',
          ),
        ),
      );
    }
  };

  return (
    <PageLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card loading={isLoading}>
          <FormHandler
            layout="vertical"
            schema={UserCommunicationPreferencesSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            <FormErrorsAlert />
            <Divider>
              {t(
                'user.settings.channels-title',
                'Canali per le comunicazioni di cortesia',
              )}
            </Divider>
            <FormItem
              name="flNotificaEmail"
              label={t('user.settings.email-channel', 'Notifica via e-mail')}
              layout={{ hideVisualLabel: true, removeLabelSpace: true }}
            >
              <SwitchInput name="flNotificaEmail" align="start">
                <Flex vertical>
                  <b>
                    {t('user.settings.email-channel', 'Notifica via e-mail')}
                  </b>
                  <span>
                    {t(
                      'user.settings.email-channel-description',
                      'Ricevi un avviso via e-mail quando è disponibile una nuova comunicazione',
                    )}
                  </span>
                </Flex>
              </SwitchInput>
            </FormItem>

            <NotificationEmailField />

            {enableAppIoChannel && (
              <FormItem
                name="flNotificaEsterna"
                label={t('user.settings.app-io-channel', 'Notifica su App IO')}
                layout={{ hideVisualLabel: true, removeLabelSpace: true }}
              >
                <SwitchInput name="flNotificaEsterna" align="start">
                  <Flex vertical>
                    <b>
                      {t('user.settings.app-io-channel', 'Notifica su App IO')}
                    </b>
                    <span>
                      {t(
                        'user.settings.app-io-channel-description',
                        'Ricevi un avviso su App IO quando è disponibile una nuova comunicazione',
                      )}
                    </span>
                  </Flex>
                </SwitchInput>
              </FormItem>
            )}

            <SubmitButton>
              {t('user.settings.save-button', 'Salva Impostazioni')}
            </SubmitButton>
          </FormHandler>
        </Card>
      </div>
    </PageLayout>
  );
}
